import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminRequest, queryString, saveBlob } from "lib/adminSiteApi";
import { hasCapability, useAdmin } from "../RequireAdmin";
import {
  ActionForm,
  Badge,
  formSchema,
  requiredText,
  text,
  select,
  revision,
  Record,
  Loading,
  useRemote,
} from "./Controls";
import {
  Collection,
  Lookup,
  ReadOnly,
  RelatedRecords,
  SimpleAction,
} from "./Records";
import { ANNOTATE, ROLES } from "./catalog";
export const canExport = (p) =>
  hasCapability(p, "crm.export") ||
  hasCapability(p, "recruiting.export") ||
  hasCapability(p, "audit.read");
export function GrantForm({ record = {}, onDone }) {
  const [picked, setPicked] = useState({
    customerId: record.customerId || "",
    resourceId: record.resourceId || "",
  });
  const [names, setNames] = useState({});
  return (
    <>
      <section className="aw-panel">
        <h2>Choose customer and resource</h2>
        <Lookup
          domain="customers"
          label="Find customer"
          onSelect={(r) => {
            setPicked((p) => ({ ...p, customerId: r.id }));
            setNames((n) => ({ ...n, customerId: r.email || r.name }));
          }}
        />
        <Lookup
          domain="resources"
          path="/admin/access-resources"
          label="Find resource"
          onSelect={(r) => {
            setPicked((p) => ({ ...p, resourceId: r.id }));
            setNames((n) => ({ ...n, resourceId: r.title }));
          }}
        />
      </section>
      <ActionForm
        title="Manage website file access"
        schema={formSchema(
          {
            customerId: requiredText(),
            resourceId: requiredText(),
            status: select("active", "revoked"),
            expiresAt: { type: "string", maxLength: 30 },
            reason: requiredText(),
          },
          ["customerId", "resourceId", "status", "reason"],
        )}
        initial={{
          ...picked,
          status: record.status || "active",
          expiresAt: record.expiresAt
            ? new Date(record.expiresAt).toISOString()
            : "",
        }}
        additions={picked}
        choices={{
          customerId: picked.customerId
            ? [
                {
                  id: picked.customerId,
                  name: names.customerId || picked.customerId,
                },
              ]
            : [],
          resourceId: picked.resourceId
            ? [
                {
                  id: picked.resourceId,
                  name: names.resourceId || picked.resourceId,
                },
              ]
            : [],
        }}
        description="Grant or revoke a free website resource. Optional expiry uses an ISO date, e.g. 2027-01-31T18:00:00Z. This never grants paid TMS access."
        submit={(body, key) => {
          const expiry = body.expiresAt ? Date.parse(body.expiresAt) : null;
          if (body.expiresAt && !Number.isFinite(expiry))
            throw new Error("Enter a valid expiry date and time.");
          return adminRequest("/admin/grants", {
            method: "POST",
            key,
            body: { ...body, expiresAt: expiry },
          });
        }}
        onDone={onDone}
      />
    </>
  );
}
export function ExportForm({ domain, onDone }) {
  const profile = useAdmin();
  const domains = [
    "contacts",
    "companies",
    "submissions",
    "applications",
    "assessments",
  ].filter((d) =>
    hasCapability(
      profile,
      ["applications", "assessments"].includes(d)
        ? "recruiting.export"
        : "crm.export",
    ),
  );
  const [selected, setSelected] = useState(domain || domains[0]);
  return (
    <>
      <label>
        Export source
        <select value={selected} onChange={(e) => setSelected(e.target.value)}>
          {domains.map((d) => (
            <option key={d}>{d}</option>
          ))}
        </select>
      </label>
      <ActionForm
        key={selected}
        title="Create a private export"
        description="Exports contain at most 10,000 records. Apply a status filter for a narrower export. Only you can download your completed export; the file expires after one hour."
        schema={formSchema(
          ["contacts", "companies", "submissions", "applications"].includes(
            selected,
          )
            ? { status: text(100) }
            : { applicationId: text(128) },
        )}
        button="Queue export"
        submit={(filters, key) =>
          adminRequest("/admin/exports", {
            method: "POST",
            key,
            body: {
              domain: selected,
              filters: Object.fromEntries(
                Object.entries(filters).filter(([, v]) => v),
              ),
            },
          })
        }
        onDone={onDone}
      />
    </>
  );
}
function Assignees({ domain, children }) {
  const [cursor, setCursor] = useState(null),
    [previous, setPrevious] = useState([]);
  const state = useRemote(
    `/admin/assignees/${domain}?${queryString({ after: cursor })}`,
  );
  const options = [
    { id: "", name: "Unassigned" },
    ...previous,
    ...(state.data?.items || []),
  ];
  return (
    <>
      <Loading state={state}>{children(options)}</Loading>
      {state.data?.next && (
        <button
          className="aw-button"
          onClick={() => {
            setPrevious(options.filter((o) => o.id));
            setCursor(state.data.next);
          }}
        >
          Load more staff
        </button>
      )}
    </>
  );
}
// The full "Review and assign" form (any staff member, tags, archive); the Inbox folds it under "More options".
export function AnnotationForm({ domain, record, onDone }) {
  return (
    <Assignees domain={domain}>
      {(choices) => (
        <ActionForm
          title="Review and assign"
          key={record.revision}
          schema={formSchema(
            {
              revision,
              status: select(
                "new",
                "reviewing",
                "contacted",
                "qualified",
                "closed",
                "archived",
              ),
              assignedTo: text(128),
              tags: { type: "array", items: text(200), maxItems: 40 },
              note: text(10000),
            },
            ["revision"],
          )}
          initial={{
            revision: record.revision,
            status: record.status,
            assignedTo: record.assignedTo || "",
            tags: record.tags || [],
          }}
          choices={{ assignedTo: choices }}
          submit={(body, key) =>
            adminRequest(`/admin/${domain}/${record.id}`, { method: "PATCH", body, key })
          }
          onDone={onDone}
        />
      )}
    </Assignees>
  );
}
export function DomainActions({ domain, record, onRefresh }) {
  const profile = useAdmin(),
    nav = useNavigate(),
    id = record.id;
  const changed = (result) => onRefresh(result);
  const update =
    (path, method = "PATCH") =>
    (body, key) =>
      adminRequest(path, { method, body, key });
  return (
    <>
      {ANNOTATE.includes(domain) && (
        <>
          {hasCapability(
            profile,
            domain === "applications" ? "recruiting.manage" : "crm.manage",
          ) && (
            <AnnotationForm domain={domain} record={record} onDone={changed} />
          )}
          <RelatedRecords
            title="Staff notes"
            path={`/admin/${domain}/${id}/notes`}
            refresh={record.revision}
          />
          {domain === "applications" && (
            <Collection
              domain="assessments"
              params={{ applicationId: id }}
              compact
            />
          )}
          {domain === "contacts" && (
            <>
              <Collection
                domain="submissions"
                params={{ contactId: id }}
                compact
              />
              {hasCapability(profile, "crm.manage") && (
                <ActionForm
                  title="Review a possible duplicate"
                  schema={formSchema(
                    {
                      secondId: requiredText(128),
                      decision: select("distinct", "duplicate"),
                      reason: requiredText(),
                    },
                    ["secondId", "decision", "reason"],
                  )}
                  initial={{ decision: "distinct" }}
                  description="Copy the other contact's record ID. This records a review; it does not merge identities or paid access."
                  submit={(body, key) =>
                    adminRequest("/admin/duplicate-review", {
                      method: "POST",
                      key,
                      body: { ...body, firstId: id },
                    })
                  }
                />
              )}
            </>
          )}
          {record.submissionId && (
            <Link
              className="aw-link"
              to={`/admin/submissions/${record.submissionId}`}
            >
              Open original enquiry
            </Link>
          )}
          {hasCapability(
            profile,
            ["applications"].includes(domain)
              ? "recruiting.export"
              : "crm.export",
          ) && (
            <Link
              className="aw-link"
              to={`/admin/exports/new?domain=${domain}`}
            >
              Export this domain
            </Link>
          )}
        </>
      )}
      {domain === "customers" && (
        <>
          {hasCapability(profile, "customers.manage") && (
            <>
              <ActionForm
                title="Customer account status"
                schema={formSchema(
                  {
                    revision,
                    status: select("active", "suspended"),
                    reason: requiredText(),
                  },
                  ["revision", "status", "reason"],
                )}
                initial={{ revision: record.revision, status: record.status }}
                description="Changing status invalidates existing website sessions. Suspension blocks website file access."
                submit={update(`/admin/customers/${id}/status`)}
                onDone={changed}
              />
              <RelatedRecords
                title="Website sessions"
                path={`/admin/customers/${id}/sessions`}
                refresh={record.revision}
                actions={(r) =>
                  !r.revokedAt && (
                    <SimpleAction
                      title="Revoke this session"
                      action={() =>
                        adminRequest(
                          `/admin/customers/${id}/sessions/${r.id}/revoke`,
                          { method: "POST", body: {} },
                        )
                      }
                      onDone={changed}
                    />
                  )
                }
              />
            </>
          )}
          {hasCapability(profile, "grants.manage") && (
            <GrantForm record={{ customerId: id }} onDone={changed} />
          )}
          <Collection
            domain="grants"
            params={{ customerId: id }}
            refresh={record.revision}
            compact
          />
          <Collection
            domain="grant-history"
            params={{ customerId: id }}
            refresh={record.revision}
            compact
          />
        </>
      )}
      {domain === "grants" && (
        <>
          {hasCapability(profile, "grants.manage") && (
            <GrantForm record={record} onDone={changed} />
          )}
          <RelatedRecords
            title="Grant and revocation history"
            path={`/admin/grant-history?${queryString({ customerId: record.customerId, resourceId: record.resourceId })}`}
            refresh={record.revision}
          />
        </>
      )}
      {domain === "support" && (
        <>
          <RelatedRecords
            title="Conversation"
            path={`/admin/support/${id}/messages`}
            refresh={record.revision}
          />
          <ActionForm
            title="Reply to customer"
            description="A saved reply queues an email. Check Email operations for provider acceptance; it does not prove inbox delivery."
            schema={formSchema({ message: requiredText(10000) }, ["message"])}
            submit={update(`/admin/support/${id}/messages`, "POST")}
            onDone={changed}
          />
          <Assignees domain="support">
            {(choices) => (
              <ActionForm
                title="Manage conversation"
                schema={formSchema(
                  {
                    revision,
                    status: select("open", "waiting", "closed"),
                    assignedTo: text(128),
                  },
                  ["revision", "status"],
                )}
                initial={{
                  revision: record.revision,
                  status: record.status,
                  assignedTo: record.assignedTo || "",
                }}
                choices={{ assignedTo: choices }}
                submit={update(`/admin/support/${id}/status`)}
                onDone={changed}
              />
            )}
          </Assignees>
        </>
      )}
      {["email", "sync", "scans"].includes(domain) && (
        <>
          {domain === "sync" && (
            <>
              <section className="aw-panel">
                <h2>Reconciliation evidence</h2>
                <p>
                  Review the saved source payload, receiver receipt and
                  canonical mapping. Retry only after resolving the recorded
                  failure. A queued retry is not a successful synchronization.
                </p>
                <ReadOnly path={`/admin/sync/${id}/payload`} />
              </section>
              <Collection
                domain="mappings"
                params={{ entityId: record.entityId }}
                compact
              />
            </>
          )}
          {(domain === "sync"
            ? ["retry", "dead_letter", "blocked_configuration"]
            : ["retry", "dead_letter"]
          ).includes(record.status) &&
            hasCapability(
              profile,
              domain === "sync"
                ? "integrations.retry"
                : domain === "scans"
                  ? "resources.manage"
                  : "email.manage",
            ) && (
              <ActionForm
                title="Retry failed job"
                description="This returns the job to its queue. A configured worker must process it."
                schema={formSchema({ reason: requiredText() }, ["reason"])}
                button="Queue retry"
                submit={update(
                  `/admin/jobs/${domain === "scans" ? "scan" : domain}/${id}/retry`,
                  "POST",
                )}
                onDone={changed}
              />
            )}
        </>
      )}
      {domain === "versions" && (
        <>
          <Collection domain="scans" params={{ versionId: id }} compact />
          {record.uploadKind === "media" &&
            record.status === "ready" &&
            hasCapability(profile, "resources.manage") && (
              <SimpleAction
                title="Publish checked public media"
                action={() =>
                  adminRequest(`/admin/media/${id}/publish`, {
                    method: "POST",
                    body: {},
                  })
                }
                onDone={() => nav(`/admin/media/${id}`)}
              />
            )}
        </>
      )}
      {domain === "team" && (
        <ActionForm
          title="Membership and permissions"
          schema={formSchema(
            {
              revision,
              displayName: text(200),
              role: select(...ROLES),
              active: { type: "boolean" },
            },
            ["revision", "role", "active"],
          )}
          initial={{
            revision: record.revision || 0,
            displayName: record.displayName || "",
            role: record.role,
            active: record.active,
          }}
          description="An active owner must remain. You cannot change your own role. Permission changes take effect on the next server request."
          submit={update(`/admin/team/${id}`, "PUT")}
          onDone={changed}
        />
      )}
      {domain === "team" && !["owner", "super_admin"].includes(record.role) && hasCapability(profile, "team.manage") && (
        <ActionForm
          title="Prepare and release paperwork"
          schema={formSchema({ revision, enabled: { type: "boolean" } }, ["revision", "enabled"])}
          initial={{ revision: record.revision || 0, enabled: (record.extraCapabilities || []).includes("commerce.deliver") }}
          description="When switched on, this person can choose or upload the files for a paid paperwork order and release it to the buyer. Switching it off works at once."
          submit={(body, key) => adminRequest(`/admin/team/${id}/extra-rights`, { method: "PUT", key, body: { capability: "commerce.deliver", enabled: body.enabled === true, revision: body.revision } })}
          onDone={changed}
        />
      )}
      {domain === "privacy" && (
        <ActionForm
          title="Review privacy request"
          schema={formSchema(
            {
              revision,
              status: select(
                "pending_review",
                "in_progress",
                "completed",
                "declined",
              ),
              resolution: requiredText(5000),
            },
            ["revision", "status", "resolution"],
          )}
          initial={{
            revision: record.revision,
            status: record.status,
            resolution: record.resolution || "",
          }}
          description="Record the work actually performed. Completing a review does not automatically delete data or external copies."
          submit={update(`/admin/privacy/${id}`)}
          onDone={changed}
        />
      )}
      {domain === "settings" && (
        <SettingsForm id={id} record={record} onDone={changed} />
      )}
      {domain === "exports" && record.status === "completed" && (
        <SimpleAction
          title="Download private export"
          action={async () => {
            const r = await adminRequest(`/admin/exports/${id}/ticket`, {
              method: "POST",
              body: {},
            });
            const file = await adminRequest("/admin/exports/download", {
              method: "POST",
              body: { ticket: r.data.ticket },
              blob: true,
            });
            saveBlob(file.data, `magellan-${record.domain}-export.json`);
          }}
        />
      )}
    </>
  );
}
export function SettingsForm({ id, record = {}, onDone }) {
  const schema =
    id === "general"
      ? formSchema(
          {
            companyName: requiredText(200),
            supportEmail: { type: "string", format: "email" },
          },
          ["companyName", "supportEmail"],
        )
      : id === "resources"
        ? formSchema({ defaultCategory: requiredText(100) }, [
            "defaultCategory",
          ])
        : formSchema({
            proposedDays: { type: "integer", minimum: 1, maximum: 3650 },
          });
  return (
    <ActionForm
      title={`Edit ${id} settings`}
      schema={schema}
      initial={record.value || {}}
      description={
        id === "retention"
          ? "Proposal only. Automatic deletion remains disabled."
          : "Non-secret website settings. These do not deploy or change runtime credentials."
      }
      submit={(value, key) =>
        adminRequest(`/admin/settings/${id}`, {
          method: "PUT",
          key,
          body: {
            revision: record.revision || 0,
            value: id === "retention" ? { ...value, enabled: false } : value,
          },
        })
      }
      onDone={onDone}
    />
  );
}
export function CreateOperation({ domain }) {
  const nav = useNavigate(),
    params = new URLSearchParams(window.location.search);
  const done = (r) => nav(`/admin/${domain}/${r.data.id}`);
  if (domain === "grants") return <GrantForm onDone={done} />;
  if (domain === "team" || domain === "invitations")
    return (
      <ActionForm
        title="Invite a staff member"
        description="An invitation is emailed to this address. They must sign in with the same email and explicitly accept it before receiving access."
        schema={formSchema(
          {
            email: { type: "string", format: "email" },
            role: select(...ROLES),
          },
          ["email", "role"],
        )}
        initial={{ role: "readonly" }}
        button="Send invitation"
        submit={(body, key) =>
          adminRequest("/admin/team/invitations", { method: "POST", key, body })
        }
        onDone={() => nav("/admin/invitations")}
      />
    );
  if (domain === "exports")
    return <ExportForm domain={params.get("domain")} onDone={done} />;
  if (domain === "support")
    return (
      <ActionForm
        title="Start a support conversation"
        schema={formSchema(
          {
            customerId: requiredText(128),
            subject: requiredText(200),
            message: requiredText(10000),
          },
          ["customerId", "subject", "message"],
        )}
        submit={(body, key) =>
          adminRequest("/admin/support", { method: "POST", key, body })
        }
        onDone={done}
      />
    );
  return (
    <p>
      This area records server-generated activity. There is no manual create
      action.
    </p>
  );
}
export function Overview() {
  const [tick, setTick] = useState(0),
    state = useRemote("/admin/dashboard", tick);
  return (
    <>
      <button className="aw-button" onClick={() => setTick(tick + 1)}>
        Refresh overview
      </button>
      <Loading state={state}>
        {state.data && (
          <>
            <div className="aw-metrics">
              {Object.entries(state.data.counts || {}).map(([k, v]) => (
                <Link key={k} to={`/admin/${k}`} className="aw-metric">
                  <span>{k.replace(/-/g, " ")}</span>
                  <strong>{v}</strong>
                  <small>View records →</small>
                </Link>
              ))}
            </div>
            <section className="aw-panel">
              <h2>Service readiness</h2>
              <div className="aw-status-grid">
                {[
                  ["TMS synchronization", state.data.tms],
                  ["Transactional email", state.data.email],
                  ["File scanning", state.data.scanner],
                ].map(([name, value]) => (
                  <div key={name}>
                    <h3>{name}</h3>
                    <Badge
                      value={value.configured ? "configured" : "not_connected"}
                    />
                    <p>
                      {value.configured
                        ? "Configured on the server; inspect job receipts for outcomes."
                        : "Not connected. Queued work remains saved."}
                    </p>
                  </div>
                ))}
              </div>
              {state.data.tms.contract && (
                <p>TMS contract: {state.data.tms.contract}</p>
              )}
            </section>
            <section className="aw-panel">
              <h2>Publishing status</h2>
              <Record data={state.data.publication} />
              <p>
                Content saves and static website publication are separate. No
                deployment can be triggered here.
              </p>
            </section>
          </>
        )}
      </Loading>
    </>
  );
}
